import { useState } from 'react';
import {
  Actions,
  Async,
  FSpread,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { useSubscriptionBundlesModel } from '../model/subscriptionBundles.model';
import {
  BundlesCards,
  BundlesTable,
  toBundleCards,
  toTableSections,
} from './subscriptionBundle.vm.mapper';
import { ToggleSubscriptionBundlePayload } from '../model/bundle.model.mapper';
import { mixTrack } from '@services/mixpanel';

export type BundlesVMData = {
  showSubscriptionBundles: boolean;
  setEditMode: () => void;
  cancel: () => void;
  save: () => void;
  list: BundlesTable | null;
  cards: BundlesCards;
  searchText: string;
  search: (value: string) => void;
  editMode: boolean;
};

type PayloadMap = {
  search: string;
  toggleListSection: null;
  toggleProgramsSection: null;
  toggleFeaturesSection: null;
  toggleBundleContent: ToggleSubscriptionBundlePayload;
  toggleBundleFreeTrial: {
    bundleId: number;
    type: 'monthly' | 'annual';
  };
  setEditMode: null;
  cancelEdit: null;
  saveChanges: null;
};

export type BundlesVMAction = Actions<PayloadMap>;
export type BundlesVMDispatcher = FSpread<PayloadMap>;

export type SubscriptionBundlesVM = BundlesVMData & {
  dispatch: BundlesVMDispatcher;
};

export type BundleVMState = {
  searchText: string;
  listSectionOpen: boolean;
  programsSectionOpen: boolean;
  featuresSectionsOpen: boolean;
  contentSectionsOpen: boolean;
  isEditMode: boolean;
};

export const useSubscriptionBundlesVM = ({
  creatorId,
}: {
  creatorId: number;
}): Async<SubscriptionBundlesVM> => {
  const [state, setState] = useState<BundleVMState>({
    searchText: '',
    listSectionOpen: false,
    programsSectionOpen: true,
    featuresSectionsOpen: true,
    contentSectionsOpen: true,
    isEditMode: false,
  });

  const model = useSubscriptionBundlesModel({ creatorId });
  if (model.type !== 'fetched') return model;

  const {
    subscriptionPrograms,
    allFeatures,
    sellableContent,
    subscriptionBundles,
    dispatch: modelDispatch,
  } = model.value;

  const dispatch: BundlesVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as BundlesVMAction;
    switch (action.type) {
      case 'search':
        setState((prev) => ({ ...prev, searchText: action.payload }));
        return null;
      case 'toggleListSection':
        setState((prev) => ({
          ...prev,
          listSectionOpen: !prev.listSectionOpen,
        }));
        return null;
      case 'toggleProgramsSection':
        setState((prev) => ({
          ...prev,
          programsSectionOpen: !prev.programsSectionOpen,
        }));
        return null;
      case 'toggleFeaturesSection':
        setState((prev) => ({
          ...prev,
          featuresSectionsOpen: !prev.featuresSectionsOpen,
        }));
        return null;
      case 'setEditMode':
        setState((prev) => ({
          ...prev,
          isEditMode: true,
          listSectionOpen: true,
        }));
        return null;
      case 'cancelEdit':
        setState((prev) => ({
          ...prev,
          isEditMode: false,
        }));
        modelDispatch(action.type);
        return null;
      case 'saveChanges':
        setState((prev) => ({
          ...prev,
          isEditMode: false,
        }));
        modelDispatch(action.type);
        mixTrack({
          type: 'Subscription offer updated',
        });
        return null;
      case 'toggleBundleContent':
        modelDispatch(action.type, action.payload);
        return null;
      case 'toggleBundleFreeTrial':
        mixTrack({
          type: '7 day free trial changed',
        });
        modelDispatch(action.type, action.payload);
        return null;
    }
  };

  const filteredSubscriptionPrograms = subscriptionPrograms.filter((sp) =>
    sp.name.toLocaleLowerCase().includes(state.searchText.toLocaleLowerCase()),
  );
  const filteredAllFeatures = allFeatures.filter((f) =>
    f.name.toLocaleLowerCase().includes(state.searchText.toLocaleLowerCase()),
  );
  const filteredAllContent = sellableContent.filter((f) =>
    f.name.toLocaleLowerCase().includes(state.searchText.toLocaleLowerCase()),
  );

  return toAsyncFetched<SubscriptionBundlesVM>({
    dispatch,
    showSubscriptionBundles: model.value.hasBundleSubscriptions,
    setEditMode: () => dispatch('setEditMode'),
    cancel: () => dispatch('cancelEdit'),
    save: () => dispatch('saveChanges'),
    searchText: state.searchText,
    search: (value) => dispatch('search', value),
    cards: !subscriptionBundles
      ? []
      : toBundleCards(dispatch, subscriptionBundles),
    list: !subscriptionBundles
      ? null
      : toTableSections(
          dispatch,
          state,
          filteredSubscriptionPrograms,
          filteredAllFeatures,
          filteredAllContent,
          subscriptionBundles,
        ),
    editMode: state.isEditMode,
  });
};
